import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

type AuthStep = 'initial' | 'otp';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setError } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Scroll to top on page load/reload
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState<AuthStep>('initial');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setLocalError] = useState('');

  // OTP input refs
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check for error in URL params (e.g., from Google OAuth redirect)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setLocalError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setLocalError('');

    try {
      const response = await fetch(`${API_URL}/auth/google`);
      const data = await response.json() as { success: boolean; data?: { url: string }; error?: string };

      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error(data.error || 'Failed to initiate Google sign-in');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign-in failed';
      setLocalError(message);
      setIsGoogleLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (!email) {
      setLocalError('Please enter your email');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setStep('otp');
        // Focus first OTP input
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setLocalError(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      setLocalError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtpCode(newOtp);
      handleVerifyOTP(pastedData);
    }
  };

  const handleVerifyOTP = async (code?: string) => {
    const verifyCode = code || otpCode.join('');

    if (verifyCode.length !== 6) {
      setLocalError('Please enter the 6-digit code');
      return;
    }

    setIsLoading(true);
    setLocalError('');

    try {
      const response = await fetch(`${API_URL}/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verifyCode }),
      });

      const data = await response.json() as {
        success: boolean;
        error?: string;
        data?: {
          user: Parameters<typeof setUser>[0];
          token: string;
          isNewUser: boolean;
        };
      };

      if (data.success && data.data) {
        setUser(data.data.user, data.data.token);
        navigate('/dashboard');
      } else {
        setLocalError(data.error || 'Invalid verification code');
        setOtpCode(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setLocalError(message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpCode(['', '', '', '', '', '']);
    setLocalError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (!data.success) {
        setLocalError(data.error || 'Failed to resend code');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend code';
      setLocalError(message);
    } finally {
      setIsLoading(false);
      otpRefs.current[0]?.focus();
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Back Button - Fixed at top */}
      <AnimatePresence>
        {step === 'otp' && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="p-4 sm:p-6"
          >
            <button
              type="button"
              onClick={() => { setStep('initial'); setOtpCode(['', '', '', '', '', '']); setLocalError(''); }}
              className={`flex items-center space-x-1 text-sm ${isDark ? 'text-text-secondary hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Centered */}
      <div className={`flex-1 flex items-center justify-center px-4 sm:px-6 ${step === 'otp' ? 'pt-0' : 'pt-12 sm:pt-0'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <Link to="/" className="inline-block">
              <img
                src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                alt="SweetPilot"
                className="w-40 sm:w-48 object-contain mx-auto"
              />
            </Link>
          </div>

          {/* Error Message */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 sm:mb-6 p-3 sm:p-4 bg-accent-red/10 border border-accent-red/20 rounded-xl"
              >
                <p className="text-accent-red text-sm text-center">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

        <AnimatePresence mode="wait">
          {step === 'initial' ? (
            <motion.div
              key="initial"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-5 sm:space-y-6"
            >
              {/* Header */}
              <div className="text-center">
                <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Welcome to SweetPilot
                </h1>
                <p className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Sign in or create an account
                </p>
              </div>

              {/* Google Button */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                className={`w-full flex items-center justify-center space-x-3 px-4 py-3.5 rounded-xl border transition-all ${
                  isDark
                    ? 'bg-white text-slate-900 border-white hover:bg-slate-100'
                    : 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                }`}
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                <span className="font-medium">Continue with Google</span>
              </button>

              {/* Divider */}
              <div className="relative">
                <div className={`absolute inset-0 flex items-center`}>
                  <div className={`w-full border-t ${isDark ? 'border-dark-600' : 'border-slate-200'}`} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-4 ${isDark ? 'bg-dark-900 text-text-muted' : 'bg-slate-50 text-slate-500'}`}>
                    or
                  </span>
                </div>
              </div>

              {/* Email Input */}
              <form onSubmit={handleSendOTP}>
                <div className="relative mb-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className={`w-full px-4 py-3.5 rounded-xl border transition-colors ${
                      isDark
                        ? 'bg-dark-800 border-dark-600 text-white placeholder-text-muted focus:border-accent-purple'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                    }`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-3.5 rounded-xl border transition-all font-medium ${
                    isDark
                      ? 'bg-dark-800 border-dark-600 text-white hover:bg-dark-700'
                      : 'bg-white border-slate-300 text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      <span>Continue with email</span>
                    </>
                  )}
                </button>
              </form>

              {/* Terms */}
              <p className={`text-xs text-center ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                By continuing, you agree to our{' '}
                <Link to="/terms" className="text-accent-purple hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-accent-purple hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="otp"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-5 sm:space-y-6"
            >
              {/* Header */}
              <div className="text-center">
                <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Check your email
                </h1>
                <p className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  We sent a code to <span className="font-medium break-all">{email}</span>
                </p>
              </div>

              {/* OTP Input */}
              <div className="flex justify-center gap-2 sm:gap-3">
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { otpRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold rounded-lg sm:rounded-xl border transition-colors ${
                      isDark
                        ? 'bg-dark-800 border-dark-600 text-white focus:border-accent-purple'
                        : 'bg-white border-slate-300 text-slate-900 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                    }`}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <button
                type="button"
                onClick={() => handleVerifyOTP()}
                disabled={isLoading || otpCode.some(d => !d)}
                className="btn-primary w-full flex items-center justify-center space-x-2 py-3 sm:py-3.5"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>Verify</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Resend */}
              <p className={`text-sm text-center ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-accent-purple hover:underline font-medium"
                >
                  Resend
                </button>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      </div>
    </div>
  );
}
